import React, { createContext, useContext, useEffect, useState } from "react";
import { LDProvider } from "launchdarkly-react-client-sdk";
import Error500 from "@pages/Error/Error500";
import { useUserService } from "@utils/useUsers";
import { User } from "@shared/interfaces/User";
import { ldClientSideID } from "@constants";

const clientSideID = ldClientSideID;

interface UserContextProps {
  user: User | null;
  loading: boolean;
  chatSessionId: string | null;
  setChatSessionId: (id: string | null) => void;
  refresh: number;
  setRefresh: React.Dispatch<React.SetStateAction<number>>;
  googleDriveToken: string | null;
  setGoogleDriveToken: (token: string | null) => void;
}

export const UserContext = createContext<UserContextProps | undefined>(
  undefined
);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState(false);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [refresh, setRefresh] = useState<number>(0);
  const [googleDriveToken, setGoogleDriveToken] = useState<string | null>(null);

  const { getCurrentUser } = useUserService();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error: any) {
        console.error("Error fetching user:", error);
        if (
          error.response &&
          error.response.status >= 500 &&
          error.response.status <= 599
        ) {
          setServerError(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [refresh]);

  if (serverError) return <Error500 />;
  if (loading || !user) return null;

  const ldContext = {
    kind: "user",
    key: user?.id ?? "anonymous",
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        chatSessionId,
        setChatSessionId,
        refresh,
        setRefresh,
        googleDriveToken,
        setGoogleDriveToken,
      }}
    >
      <LDProvider clientSideID={clientSideID} context={ldContext}>
        {!loading && children}
      </LDProvider>
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
